import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { getCurrentLocation } from '../../services/location.service';
import {
  CLOCK_IN,
  CLOCK_OUT,
  MY_TIME_CLOCK_SUMMARY,
} from '../../graphql/time-clock';
import { GET_IS_TIME_CLOCK_GLOBALLY_ENABLED } from '../../graphql/settings';

const OFFLINE_QUEUE_KEY = 'sweetgrass_time_clock_offline_queue';
const LOCATION_OPTIONAL_MESSAGE = 'Please enable location when possible. This punch was saved without GPS and marked as Remote Entry.';
const TIME_CLOCK_LOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 8000,
  maximumAge: 60000,
};
const PAY_PERIOD_WEEK_OPTIONS = { weekStartsOn: 1 };

const readQueue = () => {
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
};

const writeQueue = (queue) => {
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
};

const formatElapsed = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
};

const minutesToHours = (minutes) => {
  const value = Number(minutes || 0) / 60;
  return `${value.toFixed(2)}h`;
};

const isQueueableNetworkError = (error) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;

  const networkError = error?.networkError;
  if (networkError) {
    const statusCode = networkError.statusCode || networkError.status || networkError.response?.status;
    if (statusCode === 401 || statusCode === 403) return false;
    return !statusCode || statusCode === 408 || statusCode >= 500;
  }

  const message = String(error?.message || '').toLowerCase();
  return [
    'failed to fetch',
    'load failed',
    'network request failed',
    'networkerror',
    'timeout',
    'timed out',
  ].some((text) => message.includes(text));
};

export default function TimeClockWidget({ user }) {
  const [now, setNow] = useState(Date.now());
  const [isPunching, setIsPunching] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState('');
  const [offlineQueue, setOfflineQueue] = useState(readQueue);

  const weekStart = useMemo(() => format(startOfWeek(new Date(), PAY_PERIOD_WEEK_OPTIONS), 'yyyy-MM-dd'), []);
  const weekEnd = useMemo(() => format(endOfWeek(new Date(), PAY_PERIOD_WEEK_OPTIONS), 'yyyy-MM-dd'), []);

  const [clockIn] = useMutation(CLOCK_IN);
  const [clockOut] = useMutation(CLOCK_OUT);

  const globalEnabledQuery = useQuery(GET_IS_TIME_CLOCK_GLOBALLY_ENABLED, {
    skip: !user?.requiresTimeClock,
    fetchPolicy: 'network-only',
    pollInterval: user?.requiresTimeClock ? 60000 : 0,
  });

  const isGlobalSettingResolved = typeof globalEnabledQuery.data?.isTimeClockGloballyEnabled === 'boolean';
  const isTimeClockAvailable = Boolean(user?.requiresTimeClock)
    && isGlobalSettingResolved
    && globalEnabledQuery.data.isTimeClockGloballyEnabled;

  const summaryQuery = useQuery(MY_TIME_CLOCK_SUMMARY, {
    variables: { startDate: weekStart, endDate: weekEnd },
    skip: !isTimeClockAvailable,
    fetchPolicy: 'cache-and-network',
    pollInterval: isTimeClockAvailable ? 30000 : 0,
  });

  const userQueue = useMemo(
    () => offlineQueue.filter((item) => item.userId === user?.id),
    [offlineQueue, user?.id],
  );
  const lastQueuedPunch = userQueue[userQueue.length - 1];
  const hasOfflineOpenPunch = lastQueuedPunch?.action === 'CLOCK_IN';
  const hasOfflineClosePunch = lastQueuedPunch?.action === 'CLOCK_OUT';
  const activeEntry = summaryQuery.data?.myTimeClockSummary?.activeEntry;
  const isClockedIn = hasOfflineClosePunch ? false : Boolean(activeEntry || hasOfflineOpenPunch);

  const activeStartedAt = activeEntry?.clockedInAt
    && !hasOfflineClosePunch
    ? new Date(activeEntry.clockedInAt).getTime()
    : hasOfflineOpenPunch
      ? new Date(lastQueuedPunch.input.deviceTimestamp).getTime()
      : null;

  useEffect(() => {
    if (!isClockedIn) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isClockedIn]);

  const refetchSummary = summaryQuery.refetch;

  const syncOfflineQueue = useCallback(async () => {
    if (!isTimeClockAvailable) return;
    const queue = readQueue();
    if (!queue.length || !navigator.onLine) return;

    const remaining = [];
    for (let index = 0; index < queue.length; index += 1) {
      const item = queue[index];
      try {
        const mutation = item.action === 'CLOCK_IN' ? clockIn : clockOut;
        await mutation({ variables: { input: item.input } });
      } catch (error) {
        remaining.push(...queue.slice(index));
        break;
      }
    }

    writeQueue(remaining);
    setOfflineQueue(remaining);
    if (queue.length !== remaining.length) {
      toast.success('Offline time-clock punches synced');
      refetchSummary?.();
    }
  }, [clockIn, clockOut, isTimeClockAvailable, refetchSummary]);

  useEffect(() => {
    syncOfflineQueue();
    const handleOnline = () => syncOfflineQueue();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncOfflineQueue]);

  const getPunchInput = async (isOffline) => {
    setPermissionMessage('');
    const baseInput = {
      isOffline,
      source: isOffline ? 'OFFLINE_SYNC' : 'WEB',
      deviceTimestamp: new Date().toISOString(),
    };

    try {
      const location = await getCurrentLocation(TIME_CLOCK_LOCATION_OPTIONS);
      return {
        ...baseInput,
        latitude: location.latitude,
        longitude: location.longitude,
        accuracyMeters: location.accuracy,
      };
    } catch (error) {
      setPermissionMessage(LOCATION_OPTIONAL_MESSAGE);
      return baseInput;
    }
  };

  const queueOfflinePunch = (action, input) => {
    const offlineInput = {
      ...input,
      isOffline: true,
      source: 'OFFLINE_SYNC',
    };
    const nextQueue = [...readQueue(), { id: `${Date.now()}-${action}`, userId: user.id, action, input: offlineInput }];
    writeQueue(nextQueue);
    setOfflineQueue(nextQueue);
    toast.success('Punch saved offline');
  };

  const handlePunch = async () => {
    if (!isTimeClockAvailable) return;
    setIsPunching(true);
    let action;
    let input;
    try {
      const isOffline = !navigator.onLine;
      input = await getPunchInput(isOffline);
      action = isClockedIn ? 'CLOCK_OUT' : 'CLOCK_IN';

      if (isOffline) {
        queueOfflinePunch(action, input);
      } else {
        const mutation = isClockedIn ? clockOut : clockIn;
        await mutation({ variables: { input } });
        toast.success(isClockedIn ? 'Punched out' : 'Punched in');
        await summaryQuery.refetch?.();
      }
    } catch (error) {
      if (action && input && isQueueableNetworkError(error)) {
        queueOfflinePunch(action, input);
      } else if (!permissionMessage) {
        toast.error(error.message || 'Unable to save time-clock punch');
      }
    } finally {
      setIsPunching(false);
    }
  };

  if (!isTimeClockAvailable) return null;

  const elapsedLabel = activeStartedAt ? formatElapsed(now - activeStartedAt) : '00:00:00';
  const weeklyTotal = minutesToHours(summaryQuery.data?.myTimeClockSummary?.totalMinutes || 0);

  return (
    <section className="rounded-lg bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
          <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${isClockedIn ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <div className="text-sm font-medium uppercase tracking-wide text-gray-500">
              {isClockedIn ? 'Clocked In' : 'Clocked Out'}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-mono text-3xl font-semibold text-gray-900">{elapsedLabel}</span>
              <span className="text-sm text-gray-500">This week: {weeklyTotal}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={handlePunch}
            disabled={isPunching}
            className={`inline-flex min-w-[128px] items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
              isClockedIn ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            {isPunching ? 'Saving...' : isClockedIn ? 'Punch Out' : 'Punch In'}
          </button>
          <Link
            to="/dashboard/time-clock"
            className="inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            View logs
          </Link>
        </div>
      </div>

      {permissionMessage && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <div>{permissionMessage}</div>
        </div>
      )}

      {userQueue.length > 0 && (
        <div className="mt-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
          {userQueue.length} offline punch{userQueue.length === 1 ? '' : 'es'} pending sync.
        </div>
      )}
    </section>
  );
}
